import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import StashitLogo from '../components/Logo/StashitLogo';
import { API_ENDPOINTS } from '../config/api';

interface AssignedLocker {
  id: string;
  locker_number: string;
  location_name: string;
  size: string;
  status: 'available' | 'occupied' | 'maintenance';
  last_accessed: string;
}

interface Delivery {
  id: string;
  parcel_id: string;
  customer_name: string;
  locker_number: string;
  status: 'delivered' | 'collected' | 'pending';
  delivery_date: string;
  collection_date?: string;
  revenue: number;
}

interface RevenueSummary {
  total_revenue: number;
  monthly_revenue: number;
  pending_revenue: number;
  delivery_count: number;
  collection_count: number;
  errand_count?: number;
  errand_revenue?: number;
  errand_monthly_revenue?: number;
  errand_pending_revenue?: number;
}

const CourierDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignedLockers, setAssignedLockers] = useState<AssignedLocker[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [revenueSummary, setRevenueSummary] = useState<RevenueSummary>({
    total_revenue: 0,
    monthly_revenue: 0,
    pending_revenue: 0,
    delivery_count: 0,
    collection_count: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch courier data
    fetchCourierData();
  }, []);

  // Listen for errand payment marked events to refresh revenue
  useEffect(() => {
    const handlePaymentMarked = () => {
      console.log('Errand payment marked, refreshing courier revenue...');
      fetchCourierData();
    };

    const handleErrandCompleted = () => {
      console.log('Errand completed, refreshing courier revenue...');
      fetchCourierData();
    };

    window.addEventListener('errandPaymentMarked', handlePaymentMarked);
    window.addEventListener('errandCompleted', handleErrandCompleted);

    // Also refresh when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCourierData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('errandPaymentMarked', handlePaymentMarked);
      window.removeEventListener('errandCompleted', handleErrandCompleted);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchCourierData = async () => {
    try {
      if (!user?.id) return;

      // Fetch assigned lockers
      const lockersResponse = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/${user.id}/lockers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (lockersResponse.ok) {
        const lockersData = await lockersResponse.json();
        setAssignedLockers(lockersData.map((locker: any) => ({
          id: locker.locker_id,
          locker_number: locker.lockers?.locker_number || 'N/A',
          location_name: locker.lockers?.locations?.name || 'N/A',
          size: locker.lockers?.size || 'N/A',
          status: locker.lockers?.status || 'unknown',
          last_accessed: new Date(locker.assigned_at).toLocaleString()
        })));
      }

      // Fetch deliveries
      const deliveriesResponse = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/${user.id}/deliveries`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (deliveriesResponse.ok) {
        const deliveriesData = await deliveriesResponse.json();
        setDeliveries(deliveriesData.map((delivery: any) => ({
          id: delivery.id,
          parcel_id: delivery.tracking_number,
          customer_name: delivery.customer_name,
          locker_number: delivery.lockers?.locker_number || 'N/A',
          status: delivery.status,
          delivery_date: new Date(delivery.delivered_at || delivery.created_at).toLocaleString(),
          revenue: delivery.parcel_weight_kg ? delivery.parcel_weight_kg * 10 : 25.00 // Mock revenue calculation
        })));
      }

      // Fetch revenue summary
      const revenueResponse = await fetch(`${API_ENDPOINTS.BASE}/api/couriers/${user.id}/revenue`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (revenueResponse.ok) {
        const revenueData = await revenueResponse.json();
        setRevenueSummary({
          total_revenue: revenueData.total_revenue || 0,
          monthly_revenue: revenueData.monthly_revenue || 0,
          pending_revenue: revenueData.pending_revenue || 0,
          delivery_count: revenueData.delivery_count || 0,
          collection_count: revenueData.collection_count || 0,
          errand_count: revenueData.errand_count || 0,
          errand_revenue: revenueData.errand_revenue || 0,
          errand_monthly_revenue: revenueData.errand_monthly_revenue || 0,
          errand_pending_revenue: revenueData.errand_pending_revenue || 0
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching courier data:', error);
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'occupied': return 'text-orange-600 bg-orange-100';
      case 'maintenance': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getDeliveryStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'text-blue-600 bg-blue-100';
      case 'collected': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <StashitLogo width={200} height={60} showTagline={false} />
        </div>
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          Courier Dashboard
        </h1>
        <p className="text-neutral-600">
          Welcome back, {user?.name}! Monitor your lockers and deliveries
        </p>
      </div>

      {/* Enhanced Analytics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Revenue Cards */}
        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-neutral-600">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">
                P{revenueSummary.total_revenue.toFixed(2)}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-green-500 bg-green-50 px-2 py-1 rounded-full">
                  +12% vs last month
                </span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-2xl">💰</span>
            </div>
          </div>
          {revenueSummary.errand_revenue && revenueSummary.errand_revenue > 0 && (
            <div className="mt-3 pt-3 border-t border-neutral-100">
              <div className="flex justify-between text-xs text-neutral-500">
                <span>Errands</span>
                <span>P{revenueSummary.errand_revenue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs text-neutral-500 mt-1">
                <span>Deliveries</span>
                <span>P{(revenueSummary.total_revenue - revenueSummary.errand_revenue).toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-neutral-600">This Month</p>
              <p className="text-2xl font-bold text-blue-600">
                P{revenueSummary.monthly_revenue.toFixed(2)}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-blue-500 bg-blue-50 px-2 py-1 rounded-full">
                  Active earnings
                </span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-2xl">📅</span>
            </div>
          </div>
          {revenueSummary.errand_monthly_revenue && revenueSummary.errand_monthly_revenue > 0 && (
            <div className="mt-3 pt-3 border-t border-neutral-100">
              <div className="w-full bg-neutral-200 rounded-full h-2 mt-2">
                <div
                  className="bg-blue-500 h-2 rounded-full"
                  style={{
                    width: `${revenueSummary.monthly_revenue > 0
                      ? (revenueSummary.errand_monthly_revenue / revenueSummary.monthly_revenue * 100)
                      : 0}%`
                  }}
                ></div>
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                {revenueSummary.monthly_revenue > 0
                  ? Math.round(revenueSummary.errand_monthly_revenue / revenueSummary.monthly_revenue * 100)
                  : 0}% from errands
              </p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-neutral-600">Success Rate</p>
              <p className="text-2xl font-bold text-purple-600">
                {revenueSummary.delivery_count > 0
                  ? Math.round((deliveries.filter(d => d.status === 'collected').length / revenueSummary.delivery_count) * 100)
                  : 0}%
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-purple-500 bg-purple-50 px-2 py-1 rounded-full">
                  {deliveries.filter(d => d.status === 'collected').length} successful
                </span>
              </div>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <span className="text-2xl">📈</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <div className="flex justify-between text-xs text-neutral-500">
              <span>Total Jobs</span>
              <span>{revenueSummary.delivery_count + (revenueSummary.errand_count || 0)}</span>
            </div>
            <div className="flex justify-between text-xs text-neutral-500 mt-1">
              <span>Completed</span>
              <span>{deliveries.filter(d => d.status === 'collected').length}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-neutral-600">Avg. per Delivery</p>
              <p className="text-2xl font-bold text-orange-600">
                P{revenueSummary.delivery_count > 0
                  ? (revenueSummary.total_revenue / revenueSummary.delivery_count).toFixed(2)
                  : '0.00'}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-orange-500 bg-orange-50 px-2 py-1 rounded-full">
                  Earnings per job
                </span>
              </div>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <span className="text-2xl">🎯</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-neutral-100">
            <div className="text-xs text-neutral-500">
              Based on {revenueSummary.delivery_count} deliveries
            </div>
          </div>
        </div>
      </div>

      {/* Performance Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center">
            <span className="text-xl mr-2">⚡</span>
            Performance Metrics
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Response Time</span>
              <span className="text-sm font-medium text-neutral-800">~12 minutes</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Customer Rating</span>
              <div className="flex items-center">
                <span className="text-sm font-medium text-neutral-800 mr-1">4.8</span>
                <span className="text-yellow-400">⭐</span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Active Lockers</span>
              <span className="text-sm font-medium text-neutral-800">{assignedLockers.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Completion Rate</span>
              <span className="text-sm font-medium text-green-600">
                {revenueSummary.delivery_count > 0
                  ? Math.round((deliveries.filter(d => d.status === 'collected').length / revenueSummary.delivery_count) * 100)
                  : 0}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center">
            <span className="text-xl mr-2">📊</span>
            Revenue Breakdown
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Delivery Revenue</span>
              <span className="text-sm font-medium text-neutral-800">
                P{(revenueSummary.total_revenue - (revenueSummary.errand_revenue || 0)).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-neutral-600">Errand Revenue</span>
              <span className="text-sm font-medium text-neutral-800">
                P{(revenueSummary.errand_revenue || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center border-t border-neutral-100 pt-2">
              <span className="text-sm font-medium text-neutral-800">Total Revenue</span>
              <span className="text-sm font-bold text-green-600">
                P{revenueSummary.total_revenue.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Lockers */}
      <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-neutral-800">
            My Assigned Lockers
          </h2>
          <button className="btn-primary">
            Assign New Lockers
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assignedLockers.map((locker) => (
            <div key={locker.id} className="border border-neutral-200 rounded-lg p-4 hover:shadow-medium transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-neutral-800">
                    Locker {locker.locker_number}
                  </h3>
                  <p className="text-sm text-neutral-600">{locker.location_name}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(locker.status)}`}>
                  {locker.status}
                </span>
              </div>

              <div className="space-y-2 text-sm text-neutral-600">
                <p><span className="font-medium">Size:</span> {locker.size}</p>
                <p><span className="font-medium">Last Access:</span> {locker.last_accessed}</p>
              </div>

              <div className="mt-4 flex space-x-2">
                <button className="btn-secondary text-sm flex-1">
                  Access Locker
                </button>
                <button className="btn-tertiary text-sm">
                  Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Deliveries */}
      <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-neutral-800">
            Recent Deliveries
          </h2>
          <button className="btn-secondary">
            View All
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Parcel ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Locker
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-neutral-200">
              {deliveries.map((delivery) => (
                <tr key={delivery.id} className="hover:bg-neutral-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900">
                    {delivery.parcel_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                    {delivery.customer_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                    {delivery.locker_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDeliveryStatusColor(delivery.status)}`}>
                      {delivery.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 font-medium">
                    P{delivery.revenue.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                    <button className="text-primary-600 hover:text-primary-900 mr-3">
                      View
                    </button>
                    <button className="text-secondary-600 hover:text-secondary-900">
                      Update
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
        <h2 className="text-xl font-semibold text-neutral-800 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/courier/new-delivery')}
            className="btn-primary p-4 text-center"
          >
            <span className="text-2xl mb-2 block">📦</span>
            Make New Delivery
          </button>
          <button
            onClick={() => navigate('/courier/assign-lockers')}
            className="btn-secondary p-4 text-center"
          >
            <span className="text-2xl mb-2 block">🔒</span>
            Access Lockers
          </button>
          <button
            onClick={() => navigate('/courier/earnings')}
            className="btn-tertiary p-4 text-center"
          >
            <span className="text-2xl mb-2 block">📊</span>
            View Reports
          </button>
          <button
            onClick={() => navigate('/courier/errands')}
            className="bg-accent-500 hover:bg-accent-600 text-white p-4 text-center rounded-lg transition-colors duration-200 shadow-soft"
          >
            <span className="text-2xl mb-2 block">🚗</span>
            Manage Errands
          </button>
        </div>
      </div>

      {/* Help Section for New Couriers */}
      {assignedLockers.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-2xl">ℹ️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-blue-800">
                Welcome to Your Courier Dashboard!
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p className="mb-2">
                  To get started with your courier operations:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Go to <strong>Assign Lockers</strong> to select and rent lockers for your operations</li>
                  <li>Once lockers are assigned, you can start creating deliveries</li>
                  <li>Monitor your revenue and delivery statistics here</li>
                  <li>If you registered a courier company but can't see it here, contact support to link your accounts</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourierDashboard;
